import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, ClipboardCheck, ChevronDown } from "lucide-react";
import { formatINR } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const LEVEL2 = new Set(["introducer", "custodian", "guarantor"]);

const Chip = ({ children, color = "#B87333" }) => (
  <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border"
        style={{ color, borderColor: `${color}55`, background: `${color}0d` }}>
    {children}
  </span>
);

/**
 * Level-2 sponsor's inbox of dependent-buyer **claim** approvals.
 * A dependent Super-Stockist's commission claim lands here first; Vinci
 * Admin never sees it until the sponsor approves or rejects.
 */
export const SponsorClaimInbox = () => {
  const { user, api } = useAuth();
  const [tab, setTab] = useState("pending");
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sponsor/claim-approvals?status=${tab}`);
      setClaims(r.data || []);
    } catch { setClaims([]); }
    finally { setLoading(false); }
  }, [api, tab]);

  useEffect(() => { if (LEVEL2.has(user?.buyer_category)) load(); }, [user, load]);

  if (!user || !LEVEL2.has(user.buyer_category)) return null;

  const draftFor = (id) => drafts[id] || { commission: "", note: "" };
  const setDraft = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), [k]: v } }));

  const approve = async (c) => {
    const d = draftFor(c.id);
    const commission = d.commission ? parseFloat(d.commission) : null;
    if (d.commission && (isNaN(commission) || commission < 0 || commission > 100)) {
      toast.error("Commission override must be 0-100.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/sponsor/claim-approvals/${c.id}/approve`, {
        commission_override_pct: commission,
        note: d.note || null,
      });
      toast.success("Claim approved and forwarded to Vinci Admin.");
      setOpenId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async (c) => {
    const d = draftFor(c.id);
    if (!(d.note || "").trim()) { toast.error("A rejection note is required."); return; }
    setBusy(true);
    try {
      await api.post(`/sponsor/claim-approvals/${c.id}/reject`, { note: d.note.trim() });
      toast.success("Claim returned to the buyer with your note.");
      setOpenId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="sponsor-claim-inbox" className="mt-16">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-serif text-2xl md:text-3xl flex items-center gap-3">
          <ClipboardCheck size={22} className="text-[#D8B23E]" />
          Claim approval inbox
          {claims.length > 0 && tab === "pending" && (
            <span className="font-grotesk text-xs bg-[#D8B23E] text-black px-2 py-0.5 rounded-full">{claims.length}</span>
          )}
        </h2>
        <div className="flex items-center gap-1 rounded-full border border-white/12 p-1 bg-black/40 backdrop-blur-md" data-testid="sponsor-claim-tabs">
          {[
            { k: "pending", l: "Pending" },
            { k: "approved", l: "Approved" },
            { k: "rejected", l: "Rejected" },
            { k: "all", l: "All" },
          ].map((t) => (
            <button key={t.k}
              data-testid={`sponsor-claim-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-1.5 rounded-full font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${
                tab === t.k ? "bg-[#D8B23E] text-black" : "text-[#B8B8B0] hover:text-[#F5F5F0]"
              }`}>{t.l}</button>
          ))}
        </div>
      </div>

      <p className="font-body text-xs text-[#8A8A82] mb-5 max-w-2xl">
        Every commission claim raised by a buyer you sponsor lands here first. Approve to forward it to Vinci Admin, or reject to return it to the buyer with your reason.
      </p>

      {loading && <div className="p-6 font-body text-xs text-[#8A8A82]">Loading…</div>}
      {!loading && claims.length === 0 && (
        <div className="border border-dashed border-white/12 rounded-sm p-8 text-center">
          <p className="font-body text-sm text-[#C9C9C2]">No {tab === "all" ? "" : tab + " "}claims in your inbox.</p>
        </div>
      )}

      <div className="space-y-3">
        {claims.map((c) => {
          const isOpen = openId === c.id;
          const st = c.parent_review_status || "pending";
          const statusColor = st === "approved" ? "#2FB999" : st === "rejected" ? "#FF5A5F" : "#D8B23E";
          return (
            <div key={c.id} data-testid={`sponsor-claim-${c.id}`}
                 className="border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden"
                 style={{ boxShadow: isOpen ? `inset 0 0 0 1px ${statusColor}22` : undefined }}>
              <button type="button" onClick={() => setOpenId(isOpen ? null : c.id)}
                      className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-lg text-[#F5F5F0]">Claim {c.order_ref}</span>
                    <Chip color="#B87333">{c.buyer_email}</Chip>
                    <Chip color={statusColor}>{st}</Chip>
                    <Chip color="#8A8A82">{c.kind}</Chip>
                  </div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">
                    {c.material || "—"} · {c.quantity || "—"} · rate {c.rate_pct || 0}%
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif text-xl text-[#F5F5F0] tabular-nums">{formatINR(c.amount_claimed)}</div>
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] mt-1">Claim amount</div>
                </div>
                <ChevronDown size={18} className={`text-[#8A8A82] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="border-t border-white/[0.08] overflow-hidden">
                    <div className="p-5">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Claim details</div>
                          <div className="font-body text-xs text-[#C9C9C2] space-y-1">
                            <div>Order date: <span className="text-[#F5F5F0]">{c.order_date || "—"}</span></div>
                            <div>Order value: <span className="tabular-nums text-[#F5F5F0]">{formatINR(c.order_value)}</span></div>
                            <div>End customer / project: <span className="text-[#F5F5F0]">{c.end_customer || "—"}</span></div>
                            <div>Role in deal: <span className="text-[#F5F5F0]">{c.deal_role || "—"}</span></div>
                            {c.remarks && <div className="pt-2 border-t border-white/[0.06]"><span className="text-[#8A8A82]">Remarks:</span> {c.remarks}</div>}
                          </div>
                        </div>
                        {st === "pending" ? (
                          <div>
                            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Your decision</div>
                            <label className="block font-body text-xs text-[#8A8A82] mb-1">Commission override (%) <span className="text-[#6f6f68]">— optional</span></label>
                            <input type="number" step="0.1" min="0" max="100"
                                   value={draftFor(c.id).commission}
                                   onChange={(e) => setDraft(c.id, "commission", e.target.value)}
                                   placeholder="e.g. 2.5"
                                   data-testid={`sponsor-claim-commission-${c.id}`}
                                   className="w-full bg-transparent border border-white/12 rounded-sm px-3 py-2 font-serif text-base tabular-nums focus:border-white/40 outline-none"/>
                            <label className="block font-body text-xs text-[#8A8A82] mt-3 mb-1">Note <span className="text-[#6f6f68]">— required for reject</span></label>
                            <textarea rows={3} value={draftFor(c.id).note}
                                      onChange={(e) => setDraft(c.id, "note", e.target.value)}
                                      placeholder="Add context for the buyer or the admin desk…"
                                      data-testid={`sponsor-claim-note-${c.id}`}
                                      className="w-full bg-transparent border border-white/12 rounded-sm px-3 py-2 font-body text-sm focus:border-white/40 outline-none resize-none"/>
                            <div className="flex items-center gap-2 mt-4">
                              <button type="button" disabled={busy}
                                      data-testid={`sponsor-claim-approve-${c.id}`}
                                      onClick={() => approve(c)}
                                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2FB999] text-black font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-60">
                                <CheckCircle2 size={13} /> Approve & forward
                              </button>
                              <button type="button" disabled={busy}
                                      data-testid={`sponsor-claim-reject-${c.id}`}
                                      onClick={() => reject(c)}
                                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#FF5A5F]/60 text-[#FF5A5F] font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-60">
                                <XCircle size={13} /> Reject with note
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Decision on file</div>
                            <div className="font-body text-xs text-[#C9C9C2] space-y-1.5">
                              <div>Status: <b style={{ color: statusColor }}>{st}</b></div>
                              {c.parent_review_commission_pct != null && <div>Commission override: <b>{c.parent_review_commission_pct}%</b></div>}
                              {c.parent_review_note && (
                                <div className="pt-2 border-t border-white/[0.06]"><span className="text-[#8A8A82]">Note:</span> {c.parent_review_note}</div>
                              )}
                              {c.parent_reviewed_at && <div className="text-[#8A8A82] text-[10px]">Recorded {new Date(c.parent_reviewed_at).toLocaleString("en-IN")}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
